import {
  type EmailComposerSource,
  trackEmailComposerAction,
} from "../analytics";
import type { Editor } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import type { EditorView } from "@tiptap/pm/view";

import { toast } from "@/lib/use-toast";

import {
  insertLayoutRow,
  layoutPresets,
  pickImageFiles,
} from "./layout-presets";

/** Read width/height from an image File (very fast for local blobs). */
async function readImageDimensions(file: File) {
  const bmp = await createImageBitmap(file);
  const dims = { width: bmp.width, height: bmp.height };
  bmp.close();
  return dims;
}

/** Position of the `emailImage` placeholder with the given `uploadId`. */
function findUploadingNode(view: EditorView, uploadId: string) {
  let targetPos: number | null = null;
  view.state.doc.descendants((node, pos) => {
    if (
      targetPos === null &&
      node.type.name === "emailImage" &&
      node.attrs.uploadId === uploadId
    ) {
      targetPos = pos;
      return false;
    }
  });
  if (targetPos === null) return null;
  const node = view.state.doc.nodeAt(targetPos);
  return node ? { pos: targetPos as number, node } : null;
}

let nextUploadId = 0;

/**
 * Upload an image into the document behind a correctly-sized skeleton.
 *
 * `place` decides where the skeleton goes — at the selection for a paste, at
 * the drop point for a drag, in a row of its own for the `/` menu. The
 * skeleton becomes the image when the upload lands, or is removed if it fails.
 * Resolves once the skeleton is placed; the upload carries on after.
 */
function insertImageUpload(
  view: EditorView,
  file: File,
  upload: (file: File) => Promise<string>,
  place: (placeholder: Node) => void,
  source: EmailComposerSource,
) {
  const uploadId = `upload-${++nextUploadId}`;
  return readImageDimensions(file)
    .catch(() => ({ width: 400, height: 300 }))
    .then((dims) => {
      place(
        view.state.schema.nodes.emailImage.create({
          uploading: true,
          uploadId,
          alt: file.name,
          naturalWidth: dims.width,
          naturalHeight: dims.height,
        }),
      );

      upload(file)
        .then((url) => {
          const found = findUploadingNode(view, uploadId);
          if (found) {
            view.dispatch(
              view.state.tr.setNodeMarkup(found.pos, null, {
                ...found.node.attrs,
                src: url,
                alt: file.name,
                uploading: false,
                uploadId: "",
              }),
            );
          }
          trackEmailComposerAction("image_inserted", source);
        })
        .catch(() => {
          const found = findUploadingNode(view, uploadId);
          if (found) {
            view.dispatch(
              view.state.tr.delete(found.pos, found.pos + found.node.nodeSize),
            );
          }
          toast({ title: "Failed to upload image", variant: "error" });
        });
    });
}

/**
 * Upload several images, placing their skeletons one at a time so they land
 * in the order picked. Reading each file's size is async, and without the
 * wait whichever finished first went first.
 */
export async function insertImageUploads(
  view: EditorView,
  files: File[],
  upload: (file: File) => Promise<string>,
  place: (placeholder: Node) => void,
  source: EmailComposerSource,
) {
  for (const file of files) {
    if (view.isDestroyed) return;
    await insertImageUpload(view, file, upload, place, source);
  }
}

/**
 * Pick images and upload each into a row of its own, behind a skeleton.
 *
 * The `/` menu and the toolbar both go through here, so each image lands with
 * an empty line below it — the next image has somewhere to go, which is how a
 * stack of designed slices gets built.
 */
export async function insertImagesFromPicker(
  editor: Editor,
  source: EmailComposerSource,
) {
  const upload = editor.storage.emailImageUpload?.upload();
  if (!upload) {
    insertLayoutRow(editor, layoutPresets.image());
    return;
  }
  const files = await pickImageFiles({ multiple: true });
  if (editor.isDestroyed) return;
  await insertImageUploads(
    editor.view,
    files,
    upload,
    (placeholder) =>
      insertLayoutRow(editor, layoutPresets.image(placeholder.toJSON())),
    source,
  );
}
